import { FC } from "react";
import { Link, generatePath } from "react-router-dom";
import { STAFF_USER_ROUTE, STUDENT_ROUTE } from "../../../../app/router/routes";
import { IAuditRegularScheduleHistoryItem } from "../../model/types";
import { formatDateTime, formatLocalDate } from "../../../../widgets/scheduleCalendar/lib/scheduleDate";
import { getSlotTimeLabel, getWeekdayLabel } from "../../../../widgets/scheduleCalendar/lib/slotPattern";
import { getMoscowDateTimeParts } from "../../../../shared/lib/helpers/moscowDateTime";
import classes from "./auditHistoryList.module.scss";

interface IProps {
    items: IAuditRegularScheduleHistoryItem[];
    emptyText: string;
}

const getSlotsLabel = (item: IAuditRegularScheduleHistoryItem): string[] => (
    item.weekly_slots.map(slot => {
        const start = getSlotTimeLabel(slot.startSlot)
        const end = getSlotTimeLabel(slot.startSlot + slot.durationSlots)
        return `${getWeekdayLabel(slot.weekday)} · ${start}-${end}`
    })
)

const formatAvailabilitySlot = (startsAt: string, durationSlots: number): string => {
    const start = new Date(startsAt)

    if(Number.isNaN(start.getTime())) return startsAt

    const end = new Date(start.getTime() + durationSlots * 30 * 60 * 1000)
    const startParts = getMoscowDateTimeParts(start)
    const endParts = getMoscowDateTimeParts(end)
    const formatTime = ({hours, minutes}: {hours: number; minutes: number}) => (
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    )

    return `${formatTime(startParts)}-${formatTime(endParts)}`
}

const STATUS_LABELS: Record<IAuditRegularScheduleHistoryItem['status'], string> = {
    REGULAR_SCHEDULE_STATUS_UNSPECIFIED: 'Без статуса',
    REGULAR_SCHEDULE_STATUS_ACTIVE: 'Создание графика',
    REGULAR_SCHEDULE_STATUS_ARCHIVED: 'Удаление графика',
    REGULAR_SCHEDULE_STATUS_UPDATED: 'Изменение графика студента',
}

const STATUS_CLASSNAMES: Record<IAuditRegularScheduleHistoryItem['status'], string> = {
    REGULAR_SCHEDULE_STATUS_UNSPECIFIED: classes.statusNeutral,
    REGULAR_SCHEDULE_STATUS_ACTIVE: classes.statusActive,
    REGULAR_SCHEDULE_STATUS_ARCHIVED: classes.statusArchived,
    REGULAR_SCHEDULE_STATUS_UPDATED: classes.statusUpdated,
}

export const AuditHistoryList: FC<IProps> = ({items, emptyText}) => {
    if(!items.length){
        return <section className={classes.empty}>{emptyText}</section>
    }

    return (
        <section className={classes.list}>
            {items.map(item => {
                const studentId = Number(item.student?.id ?? 0)
                const tutorUserId = Number(item.tutor?.userId ?? 0)
                const isTutorAvailabilityHistory = item.entity_type === 'REGULAR_SCHEDULE_HISTORY_ENTITY_TYPE_TUTOR'
                const actorUserId = item.audit?.updatedByUserId || item.audit?.createdByUserId || ''
                const actorUserIdNumber = Number(actorUserId)
                const hasActorProfile = Number.isFinite(actorUserIdNumber) && actorUserIdNumber > 0
                const actorLabel = actorUserId === '0'
                    ? 'Системное обновление'
                    : (item.updated_by_full_name || (hasActorProfile ? `Сотрудник ${actorUserIdNumber}` : ''))
                const actorRoleLabel = actorUserId === '0' ? '' : item.updated_by_role_name
                const statusLabel = isTutorAvailabilityHistory
                    ? 'Изменение графика работы'
                    : item.status === 'REGULAR_SCHEDULE_STATUS_UPDATED' && actorUserId === '0'
                    ? 'Изменение графика студента (пролонгация)'
                    : STATUS_LABELS[item.status]
                const statusClassName = isTutorAvailabilityHistory
                    ? classes.statusActive
                    : STATUS_CLASSNAMES[item.status]

                return (
                    <section key={`${item.schedule_id}-${item.audit?.updatedAt ?? item.audit?.createdAt ?? 'history'}`} className={classes.item}>
                        <section className={classes.top}>
                            <section className={classes.title}>
                                <span>
                                    {item.audit?.updatedAt
                                        ? `Изменено: ${formatDateTime(item.audit.updatedAt)}`
                                        : item.audit?.createdAt
                                            ? `Создано: ${formatDateTime(item.audit.createdAt)}`
                                            : 'История изменения'
                                    }
                                </span>
                                {item.status && (
                                    <span className={`${classes.statusBadge} ${statusClassName}`}>
                                        {statusLabel}
                                    </span>
                                )}
                            </section>
                        </section>

                        <section className={classes.grid}>
                            {studentId > 0 && item.student?.fullName && (
                                <section className={classes.field}>
                                    <section className={classes.label}>Студент</section>
                                    <section className={classes.value}>
                                        <Link className={classes.link} to={generatePath(STUDENT_ROUTE.path, {id: String(studentId)})}>
                                            {studentId}. {item.student.fullName}
                                        </Link>
                                    </section>
                                </section>
                            )}
                            {tutorUserId > 0 && (item.tutor?.fullName || isTutorAvailabilityHistory) && (
                                <section className={classes.field}>
                                    <section className={classes.label}>Исполнитель</section>
                                    <section className={classes.value}>
                                        <Link className={classes.link} to={generatePath(STAFF_USER_ROUTE.path, {user_id: String(tutorUserId)})}>
                                            {tutorUserId}. {item.tutor?.fullName || 'Исполнитель'}
                                        </Link>
                                    </section>
                                </section>
                            )}
                            {(actorLabel || actorRoleLabel) && (
                                <section className={classes.field}>
                                    <section className={classes.label}>Кто изменил</section>
                                    <section className={classes.value}>
                                        {hasActorProfile
                                            ? <Link className={classes.link} to={generatePath(STAFF_USER_ROUTE.path, {user_id: String(actorUserIdNumber)})}>
                                                {actorLabel || `Сотрудник ${actorUserIdNumber}`}
                                            </Link>
                                            : (actorLabel || 'Не указан')}
                                        {actorRoleLabel ? ` · ${actorRoleLabel}` : ''}
                                    </section>
                                </section>
                            )}
                        </section>

                        {Boolean(item.weekly_slots.length) && (
                            <section className={classes.field}>
                                <section className={classes.label}>Слоты</section>
                                <section className={classes.slots}>
                                    {getSlotsLabel(item).map(slotLabel => (
                                        <section key={slotLabel} className={classes.slot}>{slotLabel}</section>
                                    ))}
                                </section>
                            </section>
                        )}

                        {isTutorAvailabilityHistory && Boolean(item.date_overrides.length) && (
                            <section className={classes.field}>
                                <section className={classes.label}>Исключения по датам</section>
                                <section className={classes.overrides}>
                                    {item.date_overrides.map(override => (
                                        <section
                                            className={classes.override}
                                            key={`${override.date.year}-${override.date.month}-${override.date.day}`}
                                        >
                                            <span className={classes.overrideDate}>{formatLocalDate(override.date)}</span>
                                            {override.available_slots.length
                                                ? <section className={classes.slots}>
                                                    {override.available_slots.map(slot => (
                                                        <section key={`${slot.starts_at}-${slot.duration_slots}`} className={classes.slot}>
                                                            {formatAvailabilitySlot(slot.starts_at, slot.duration_slots)}
                                                        </section>
                                                    ))}
                                                </section>
                                                : <span className={classes.overrideUnavailable}>Недоступен</span>
                                            }
                                            {override.comment && <span className={classes.overrideComment}>{override.comment}</span>}
                                        </section>
                                    ))}
                                </section>
                            </section>
                        )}

                        {item.comment && (
                            <section className={classes.field}>
                                <section className={classes.label}>Комментарий</section>
                                <section className={classes.comment}>{item.comment}</section>
                            </section>
                        )}
                    </section>
                )
            })}
        </section>
    )
}
